import React from "react";
import { Card, CardActionArea, CardContent, Typography } from "@material-ui/core";
import { Booking, BookingType } from "api/bookings";
import moment from "moment";
import styles from "./CalendarView.module.scss";
import i18n from "./i18n";

// prop types
interface BookingCellProps {
    booking: Booking,
    isStandardUser: boolean,
    timeOffset: number,
    onClick?: (e: React.MouseEvent) => void,
}

// booking cell in calendar view
const BookingCell = ({ booking, isStandardUser, timeOffset, onClick }: BookingCellProps) => {
    const cellHeight = parseInt(styles.cellHeight, 10);
    const cellWidth = parseInt(styles.cellWidth, 10);

    // time calculations
    const startDate = moment(booking.startDate);
    const endDate = moment(booking.endDate);
    const startDateIgnoreDST = startDate.clone().set({ dayOfYear: 1 });
    const minutesSinceMidnight = startDateIgnoreDST.diff(startDateIgnoreDST.clone().startOf("day"), "minutes");
    const durationInMinutes = endDate.diff(startDate, "minutes");
    const timeCaption = `${startDate.format("H:mm")} - ${endDate.format("H:mm")}`;

    // styling
    const getStyle = ({ blockingReason, bookingStatus, bookingType, customMaxNumParticipants, numAcceptedParticipants }: Booking) => {
        if (bookingType === BookingType.Blocking && blockingReason === "not-blocking") {
            return { border: `1px solid ${styles.bookingExternalBlockingColor}` };
        }
        if (bookingType === BookingType.Blocking && blockingReason !== "not-blocking") {
            return { background: styles.bookingBlockedColor };
        }
        if (bookingType === BookingType.Meetup) {
            return {
                border: `2px solid ${styles.bookingStandardFinalColor}`,
                transform: `translate(${5 * cellWidth / 6 + 3}px, ${(minutesSinceMidnight / 60 - timeOffset) * cellHeight}px)`,
                width: cellWidth / 6 - 6,
                zIndex: 1,
            };
        }
        if (bookingType === BookingType.Training) {
            if ((customMaxNumParticipants || customMaxNumParticipants === 0) && numAcceptedParticipants >= customMaxNumParticipants) { // event full
                return { background: styles.bookingStandardColor };
            }
            return { background: styles.bookingTrainingColor };
        }
        if (bookingStatus === "pending") {
            return { background: styles.bookingPendingColor };
        }
        if (bookingType === BookingType.Standard) {
            return { background: styles.bookingStandardColor };
        }
        return {};
    };

    // clickable
    const isClickable = !(booking.bookingType === BookingType.Blocking && booking.blockingReason === "not-blocking" && isStandardUser);

    // render
    return (
        <Card
            style={{
                display: "flex",
                height: (durationInMinutes / 60) * cellHeight,
                pointerEvents: isClickable ? "auto" : "none",
                position: "absolute",
                transform: `translate(3px, ${(minutesSinceMidnight / 60 - timeOffset) * cellHeight}px)`,
                width: cellWidth - 6, // offset to hide elevation shadow behind court name header
                ...getStyle(booking),
            }}
        >
            <CardActionArea onClick={onClick} disabled={!onClick}>
                {booking.bookingType !== BookingType.Meetup && (
                    <CardContent>
                        <Typography variant="subtitle2">{booking.description || i18n.bookingTypes[booking.bookingType]}</Typography>
                        <Typography variant="caption">{timeCaption}</Typography>
                    </CardContent>
                )}
            </CardActionArea>
        </Card>
    );
};

export default BookingCell;
